import React, { useState } from "react";
import axios from "axios";
import { Ornament, StarGlyph, StarOfIshtar } from "../Ornaments";
import { useReveal } from "../../hooks/useReveal";
import { toast } from "sonner";
import { Instagram, Mail, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_LINKS = [
  { href: "mailto:pschlachter@seededsomatics.com", icon: Mail, label: "pschlachter@seededsomatics.com", testId: "contact-email", external: false },
  { href: "https://www.seededsomatics.com", icon: Globe, label: "seededsomatics.com", testId: "contact-website-seeded", external: true },
  { href: "https://instagram.com/seeded_somatics", icon: Instagram, label: "@seeded_somatics", testId: "contact-ig-seeded", external: true },
  { href: "https://instagram.com/benjamin.equilibrium", icon: Instagram, label: "@benjamin.equilibrium", testId: "contact-ig-benjamin", external: true },
  { href: "https://instagram.com/moveminit", icon: Instagram, label: "@moveminit", testId: "contact-ig-moveminit", external: true },
];

const validateForm = (values) => {
  const er = {};
  if (!values.name.trim()) er.name = "Please share your name.";
  if (!values.email.trim()) er.email = "An email lets us reply.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) er.email = "That doesn't look like a valid email.";
  if (!values.message.trim()) er.message = "Tell us what brought you here.";
  return er;
};

const FieldError = ({ error, testId }) =>
  error ? (
    <p style={{ color: "#c9594c", fontSize: "0.85rem", marginTop: "0.4rem", fontStyle: "italic" }} data-testid={testId}>
      {error}
    </p>
  ) : null;

const SuccessPanel = ({ revealRef, inView, onReset }) => (
  <div
    ref={revealRef}
    className={`reveal ${inView ? "in-view" : ""}`}
    data-testid="connect-success"
    style={{
      background: "linear-gradient(180deg, rgba(20,16,12,0.85) 0%, rgba(10,8,6,0.95) 100%)",
      border: "1px solid rgba(201, 168, 76, 0.35)",
      padding: "3.5rem 2rem",
      textAlign: "center",
    }}
  >
    <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem" }}>
      <StarOfIshtar size={80} opacity={1} />
    </div>
    <h3 className="font-display" style={{ color: "var(--gold-bright)", fontSize: "1.8rem", marginBottom: "1rem" }}>
      Received.
    </h3>
    <p className="lead" style={{ color: "var(--text)", maxWidth: 480, margin: "0 auto" }}>
      Your transmission has arrived. One of us will respond personally — in days, not minutes.
      The work has already begun.
    </p>
    <button type="button" onClick={onReset} className="btn-ghost" style={{ marginTop: "2rem" }} data-testid="connect-send-another">
      Send Another
    </button>
  </div>
);

const ContactForm = ({ revealRef, inView, values, errors, submitting, onChange, onSubmit }) => (
  <form
    ref={revealRef}
    onSubmit={onSubmit}
    noValidate
    className={`reveal ${inView ? "in-view" : ""}`}
    data-testid="connect-form"
    style={{
      background: "linear-gradient(180deg, rgba(20,16,12,0.7) 0%, rgba(10,8,6,0.85) 100%)",
      border: "1px solid rgba(201, 168, 76, 0.18)",
      padding: "2.5rem",
      position: "relative",
    }}
  >
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem" }} className="md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="name">Name</label>
          <input
            id="name"
            name="name"
            type="text"
            value={values.name}
            onChange={onChange}
            placeholder="Your name"
            className="field-input"
            data-testid="connect-input-name"
          />
          <FieldError error={errors.name} testId="connect-error-name" />
        </div>
        <div>
          <label className="field-label" htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={values.email}
            onChange={onChange}
            placeholder="you@somewhere"
            className="field-input"
            data-testid="connect-input-email"
          />
          <FieldError error={errors.email} testId="connect-error-email" />
        </div>
      </div>
      <div>
        <label className="field-label" htmlFor="message">Tell us where you are</label>
        <textarea
          id="message"
          name="message"
          value={values.message}
          onChange={onChange}
          placeholder="What's brought you here? Live practice, the workbook — what are you ready to know about yourself?"
          className="field-textarea"
          rows={6}
          data-testid="connect-input-message"
        />
        <FieldError error={errors.message} testId="connect-error-message" />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-angled"
        style={{ width: "100%", padding: "1.15rem 2rem", marginTop: "0.5rem" }}
        data-testid="connect-submit"
      >
        {submitting ? "Sending..." : (
          <>
            Send Transmission
            <StarGlyph size={11} color="#0a0806" />
          </>
        )}
      </button>

      <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
        <p className="font-heading" style={{ color: "var(--text-dim)", fontSize: "0.6rem", letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: "0.75rem" }}>
          Ready to move now?
        </p>
        <a
          href="https://movethrus.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
          data-testid="connect-live-practice-btn"
          style={{ width: "100%", justifyContent: "center", display: "inline-flex" }}
        >
          Join Live Practice · MOVE THRUS™
        </a>
      </div>
    </div>
  </form>
);

const ContactLinks = () => (
  <div style={{ marginTop: "3rem", display: "flex", flexDirection: "column", gap: "1.5rem", alignItems: "center" }} data-testid="connect-contact-details">
    <p className="font-heading" style={{ color: "var(--text-dim)", fontSize: "0.65rem", letterSpacing: "0.4em", textTransform: "uppercase" }}>
      <span style={{ color: "var(--gold)" }}>Find the practices</span>
    </p>
    <div style={{ display: "grid", gap: "0.9rem 2.25rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, max-content))", justifyContent: "center", alignItems: "center", justifyItems: "center" }}>
      {CONTACT_LINKS.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.testId}
            href={l.href}
            className="nav-link"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.55rem" }}
            data-testid={l.testId}
            {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            <Icon size={13} /> {l.label}
          </a>
        );
      })}
    </div>
  </div>
);

export const Connect = () => {
  const head = useReveal();
  const form = useReveal();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validateForm(values);
    setErrors(er);
    if (Object.keys(er).length > 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, values);
      setSubmitted(true);
      setValues({ name: "", email: "", message: "" });
      toast.success("Transmission received. We'll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something interrupted the signal. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="connect" className="section" data-testid="connect-section" style={{ position: "relative" }}>
      <div className="connect-glow" />
      <div style={{ position: "absolute", top: 80, right: 60, opacity: 0.08 }} className="hidden md:block">
        <StarOfIshtar size={260} opacity={0.5} />
      </div>

      <div className="section-inner" style={{ maxWidth: 880 }}>
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            The First Step
          </p>
          <h2 className="section-title" data-testid="connect-title">Begin the Conversation</h2>
          <p className="italic-quote" style={{ color: "var(--text)", fontSize: "1.05rem", maxWidth: 620, margin: "0 auto", lineHeight: 1.8 }}>
            The Sumerians built their temples in steps — each level a closer approach to
            alignment. We begin with one step. Interested in live practice, the workbook,
            or working with us directly? Tell us where you are and what the body has been
            trying to say.
          </p>
        </div>

        {submitted ? (
          <SuccessPanel revealRef={form.ref} inView={form.inView} onReset={() => setSubmitted(false)} />
        ) : (
          <ContactForm
            revealRef={form.ref}
            inView={form.inView}
            values={values}
            errors={errors}
            submitting={submitting}
            onChange={handleChange}
            onSubmit={onSubmit}
          />
        )}

        <ContactLinks />
      </div>
    </section>
  );
};
